import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/contexts/AuthContext'

interface AuthGuardProps {
  children: React.ReactNode
  requireAuth?: boolean
  requiresHost?: boolean
  redirectTo?: string
  // Legacy support for employee routes (will be removed in future task)
  requiredUserGroup?: 'hosts' | 'guests' | 'employees'
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  requiresHost = false,
  redirectTo,
  requiredUserGroup, // Legacy support
}) => {
  const { isAuthenticated, userProfile, isLoading, isHost, isGuest } = useAuth()
  const location = useLocation()

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    )
  }

  // Handle authentication requirements
  if (requireAuth && !isAuthenticated) {
    const returnUrl = location.pathname + location.search
    return (
      <Navigate 
        to={`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`} 
        replace 
      />
    )
  }

  // Handle role requirements
  if (requireAuth && requiresHost && !isHost()) {
    return <Navigate to="/unauthorized" replace />
  }

  // Legacy support for employee routes (will be removed in future task)
  if (requireAuth && requiredUserGroup === 'employees') {
    // For now, allow employees to access these routes
    // This will be removed when employee frontend is separated
    if (userProfile?.userGroup !== 'employees') {
      return <Navigate to="/unauthorized" replace />
    }
  }

  // Handle redirect for authenticated users on auth pages
  if (!requireAuth && isAuthenticated && redirectTo) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export default AuthGuard